/**
 * Client Profiles Hook
 * React hook for fetching client user profiles for a specific organization
 * Uses smart caching by organizationId to avoid duplicate API calls across components
 * 
 * Note: For internal profiles, use useInternalProfiles({ organizationId }) hook
 * Note: For worker profiles, use useWorkerProfiles() hook
 */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/lib/api'
import { PROFILE_STATUS, PROFILE_TYPE } from '@/lib/constants/profile-codes'

export interface ClientProfile {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  FullName: string
  DisplayValue: string
}

interface ClientProfileApiItem {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  Contact: {
    FullName: string
  }
}

interface ClientProfilesApiResponse {
  Items: ClientProfileApiItem[]
}

interface CachedClientProfiles {
  clientProfiles: ClientProfile[]
  loading: boolean
  error: string | null
  timestamp: number
}

interface UseClientProfilesParams {
  organizationId: number
}

interface UseClientProfilesReturn {
  loading: boolean
  error: string | null
  clientProfiles: ClientProfile[]
  refresh: () => Promise<void>
}

// Global cache outside the hook - shared across all instances
const clientProfilesCache = new Map<number, CachedClientProfiles>()

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000

// Set of subscribers for cache updates
const subscribers = new Set<() => void>()

// Notify all subscribers when cache updates
const notifySubscribers = () => {
  subscribers.forEach(callback => callback())
}

export function useClientProfiles({
  organizationId
}: UseClientProfilesParams): UseClientProfilesReturn {
  const { mainApi } = useApi()
  const [, forceUpdate] = useState(0)
  
  // Subscribe to cache updates
  useEffect(() => {
    const updateCallback = () => forceUpdate(prev => prev + 1)
    subscribers.add(updateCallback)
    
    return () => {
      subscribers.delete(updateCallback)
    }
  }, [])

  const fetchClientProfiles = async (orgId: number): Promise<ClientProfile[]> => {
    // This function should only be called with valid organization IDs
    // If orgId is 0, it indicates a programming error since fetchAndCache handles that case
    if (!orgId || orgId === 0) {
      throw new Error(`fetchClientProfiles called with invalid organizationId: ${orgId}`)
    }

    console.log(`Fetching client user profiles for organization ${orgId}...`)

    const oDataParams = new URLSearchParams({
      '$expand': 'Contact',
      '$filter': `OrganizationId eq ${orgId} and ProfileTypeId eq ${PROFILE_TYPE.ORGANIZATIONAL} and (ProfileStatusId eq ${PROFILE_STATUS.ACTIVE} or ProfileStatusId eq ${PROFILE_STATUS.PENDING_CHANGE})`,
      '$select': 'Id,ProfileStatusId,ContactId,ProfileTypeId,OrganizationId,Contact/FullName'
    })
    
    const endpoint = `api/UserProfile/1/UserProfiles?${oDataParams.toString()}` // Profile Type 1 = Client
    const response = await mainApi.get<ClientProfilesApiResponse>(endpoint)
    
    const profiles = response?.Items || []
    
    const mappedProfiles: ClientProfile[] = profiles.map((p: ClientProfileApiItem) => ({
      Id: p.Id,
      ProfileStatusId: p.ProfileStatusId,
      ContactId: p.ContactId,
      ProfileTypeId: p.ProfileTypeId,
      OrganizationId: p.OrganizationId,
      FullName: p.Contact?.FullName || 'Unknown',
      DisplayValue: `${p.Contact?.FullName || 'Unknown'} - ${p.Id}`
    }))
    
    console.log(`Loaded ${mappedProfiles.length} client user profiles for organization ${orgId}`)
    return mappedProfiles
  }

  const fetchAndCache = useCallback(async (orgId: number) => {
    // This should not be called with organizationId 0 since useEffect prevents it
    if (!orgId || orgId === 0) {
      console.warn('fetchAndCache called with invalid organizationId:', orgId)
      return
    }

    console.log(`Fetching client user profiles for organization ${orgId}...`)
    
    // Set loading state in cache
    const existingCache = clientProfilesCache.get(orgId)
    clientProfilesCache.set(orgId, {
      clientProfiles: existingCache?.clientProfiles || [],
      loading: true,
      error: null,
      timestamp: Date.now()
    })
    notifySubscribers()
    
    try {
      const clientProfiles = await fetchClientProfiles(orgId)
      
      // Cache the successful results
      clientProfilesCache.set(orgId, {
        clientProfiles: clientProfiles,
        loading: false,
        error: null,
        timestamp: Date.now()
      })
      
      console.log(`Cached client user profiles for organization ${orgId}`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load client user profiles'
      
      // Cache the error state
      clientProfilesCache.set(orgId, {
        clientProfiles: [],
        loading: false,
        error: errorMessage,
        timestamp: Date.now()
      })
      
      console.error(`Failed to load client user profiles for org ${orgId}:`, err)
    }
    
    notifySubscribers()
  }, [mainApi])

  const refresh = useCallback(async () => {
    await fetchAndCache(organizationId)
  }, [fetchAndCache, organizationId])

  // Check if data needs to be fetched
  useEffect(() => {
    // Don't fetch if organizationId is 0 (no client selected yet)
    if (!organizationId || organizationId === 0) {
      return
    }
    
    const cache = clientProfilesCache.get(organizationId)
    const now = Date.now()
    
    // Fetch if no cache exists, cache is expired, or cache has an error
    if (!cache || 
        (now - cache.timestamp) > CACHE_TTL ||
        cache.error) {
      fetchAndCache(organizationId)
    }
  }, [organizationId, fetchAndCache])

  // Return current cache state or defaults
  const currentCache = clientProfilesCache.get(organizationId) || {
    clientProfiles: [],
    loading: organizationId === 0 ? false : true, // Don't show loading when no client selected
    error: null,
    timestamp: 0
  }

  return {
    loading: currentCache.loading,
    error: currentCache.error,
    clientProfiles: currentCache.clientProfiles,
    refresh
  }
} 